/**
 * Голографическая платформа под экраном пополнения.
 *
 * Чистая декорация: восемь концентрических колец, луч, вертикальные линии и
 * искры. Ни одного растрового куска — в пакете дизайна это оговорено отдельно,
 * и подложить сюда картинку значило бы потерять возможность красить сцену
 * токеном.
 *
 * Геометрия снята со строк 176–189 `design-reference/css/screens.css` — из
 * последнего слоя переопределений, а не из первого: верхние правила в этом
 * файле перекрыты, и кольца по ним вышли бы другого размера.
 *
 * Размеры переведены в проценты от исходных 360×196 один раз, здесь: наружу
 * блок остаётся обычным элементом потока с фиксированной пропорцией.
 */

/** Кольца: доля ширины, доля высоты, отступ снизу, непрозрачность. */
const RINGS = [
  { w: 96.7, h: 54.1, bottom: 9.2, opacity: 0.22 },
  { w: 87.2, h: 46.4, bottom: 13.8, opacity: 0.3 },
  { w: 76.4, h: 38.3, bottom: 18.9, opacity: 0.42 },
  { w: 63.9, h: 30.1, bottom: 24.5, opacity: 0.58 },
  { w: 51.1, h: 21.9, bottom: 29.6, opacity: 0.72 },
  { w: 38.6, h: 14.8, bottom: 34.7, opacity: 0.84 },
  { w: 26.1, h: 9.2, bottom: 38.3, opacity: 0.92 },
  { w: 13.3, h: 4.6, bottom: 40.8, opacity: 1 },
] as const;

/** Вертикальные линии луча: позиция по ширине и высота. */
const LINES = [
  { left: 36, height: 42.3 },
  { left: 43, height: 56.6 },
  { left: 50, height: 65.3 },
  { left: 57, height: 53.6 },
  { left: 64, height: 40.3 },
] as const;

/** Искры: позиция в процентах от ширины и высоты. */
const SPARKS = [
  { left: 17, bottom: 27.0 },
  { left: 29, bottom: 44.9 },
  { left: 40, bottom: 60.7 },
  { left: 60, bottom: 53.1 },
  { left: 77, bottom: 36.7 },
  { left: 24, bottom: 56.6 },
  { left: 48, bottom: 72.4 },
  { left: 69, bottom: 65.3 },
  { left: 84, bottom: 51.5 },
] as const;

export function DepositHologram() {
  return (
    <div
      aria-hidden="true"
      className="relative mx-auto aspect-[360/196] w-full overflow-hidden text-status-profit"
    >
      {/* Свечение пола. */}
      <span
        className={[
          "absolute bottom-[12%] left-1/2 h-[57%] w-[99%] -translate-x-1/2 rounded-[50%] blur-[1px]",
          "bg-[radial-gradient(ellipse_at_center,color-mix(in_srgb,currentColor_11%,transparent),transparent_70%)]",
        ].join(" ")}
      />

      {/* Луч. Трапеция вырезана clip-path, а не нарисована градиентом с
          прозрачными углами: край должен быть чётким, иначе на тёмном фоне
          виден прямоугольник. */}
      <span
        className={[
          "absolute top-[1.5%] left-1/2 h-[71%] w-[29%] -translate-x-1/2 blur-[3.5px] opacity-[0.78]",
          "bg-[linear-gradient(180deg,transparent,color-mix(in_srgb,currentColor_10%,transparent)_38%,color-mix(in_srgb,currentColor_22%,transparent)_78%,transparent)]",
          "[clip-path:polygon(36%_0,64%_0,95%_100%,5%_100%)]",
        ].join(" ")}
      />

      {LINES.map((line) => (
        <span
          key={line.left}
          className="absolute bottom-[24.5%] w-px opacity-[0.62] bg-[linear-gradient(180deg,transparent,color-mix(in_srgb,currentColor_70%,transparent),transparent)]"
          style={{
            left: `${String(line.left)}%`,
            height: `${String(line.height)}%`,
            boxShadow: "0 0 4px currentColor",
          }}
        />
      ))}

      {RINGS.map((ring) => (
        <span
          key={ring.bottom}
          className="absolute left-1/2 -translate-x-1/2 rounded-[50%] border border-current"
          style={{
            width: `${String(ring.w)}%`,
            height: `${String(ring.h)}%`,
            bottom: `${String(ring.bottom)}%`,
            opacity: ring.opacity * 0.55,
            boxShadow: "0 0 8px color-mix(in srgb, currentColor 12%, transparent)",
          }}
        />
      ))}

      {SPARKS.map((spark) => (
        <span
          key={`${String(spark.left)}-${String(spark.bottom)}`}
          className="absolute size-0.5 rounded-full bg-current"
          style={{
            left: `${String(spark.left)}%`,
            bottom: `${String(spark.bottom)}%`,
            boxShadow: "0 0 var(--glow-blur-sm) currentColor",
          }}
        />
      ))}
    </div>
  );
}
